import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Table, MatrixAxis, MatrixTable, matrixValueIndex } from '../../_models/table';
import { matrixAxisSummaryLabel, MatrixAxisSummaryContext } from '../../_models/table-matrix-axis-summary';
import { SelectorsService } from '../../_services/selectors.service';
import { CalcConfigService } from '../../_services/calc-config.service';
import { QuestionsService } from '../../_services/questions.service';
import {
    matrixAxisBinCount,
    matrixAxisLookupDriftMessage,
    syncMatrixAxisCodeLookupBands,
} from '../../_models/table-matrix.validation';
import { DecimalPipe, NgTemplateOutlet } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatInput } from '@angular/material/input';
import { MatCardContent } from '@angular/material/card';
import { FormsModule } from '@angular/forms';
import { MatrixAxisInputComponent } from './matrix-axis-input.component';

@Component({
    selector: 'app-table-matrix',
    templateUrl: './table-matrix.component.html',
    styleUrls: ['./table-matrix.component.css'],
    imports: [
        MatCardContent,
        MatButton,
        MatFormField,
        MatLabel,
        MatInput,
        FormsModule,
        DecimalPipe,
        MatrixAxisInputComponent,
        NgTemplateOutlet,
    ],
})
export class TableMatrixComponent implements OnChanges {
    @Input({ required: true }) table!: Table;
    @Input() editMode = false;
    /**
     * Rising edge (false → true): open the row/column input step once (e.g. new-table wizard chose “Advanced matrix”).
     * Parent should clear after `openRowColumnInputStepConsumed`.
     */
    @Input() openRowColumnInputStepOnce = false;
    @Output() readonly openRowColumnInputStepConsumed = new EventEmitter<void>();
    @Output() readonly matrixModelChanged = new EventEmitter<void>();
    /** `true` while the row/column axis step is open; parent may hide table-level Cancel / Save. */
    @Output() readonly rowColumnInputEditActive = new EventEmitter<boolean>();

    /** True while the user is changing row/column inputs (before confirming with "Apply row & column inputs"). */
    isEditingRowColumnInputs = false;
    /** Deep copy of `matrixTable` when opening the row/column editor; restored on cancel. */
    private matrixTableEditBackup: MatrixTable | null = null;
    /** Shape of `matrixTable.values` the last time the flat array was resized (for preserving overlapping cells). */
    private priorValueGrid = { columnCount: 0, rowCount: 0 };

    private readonly summaryContext: MatrixAxisSummaryContext = {
        selectorLabel: (keyId) => this.selectors.getSelector(keyId)?.label,
        formulaName: (formulaId) => this.calcConfig.getFormula(formulaId)?.name,
    };

    constructor(
        private selectors: SelectorsService,
        private calcConfig: CalcConfigService,
        private questions: QuestionsService,
    ) {}

    get lookupDriftWarnings(): string[] {
        const mt = this.table?.matrixTable;
        if (!mt || !this.hasRowAndColumnInputs || this.isEditingRowColumnInputs) {
            return [];
        }
        const warnings: string[] = [];
        const rowsWarning = matrixAxisLookupDriftMessage('Rows', mt.rows, this.codeChoiceValues(mt.rows));
        if (rowsWarning) {
            warnings.push(rowsWarning);
        }
        const columnsWarning = matrixAxisLookupDriftMessage('Columns', mt.columns, this.codeChoiceValues(mt.columns));
        if (columnsWarning) {
            warnings.push(columnsWarning);
        }
        return warnings;
    }

    get hasRowAndColumnInputs(): boolean {
        const mt = this.table?.matrixTable;
        if (!mt) {
            return false;
        }
        const columns = !!(mt.columns.keyId || mt.columns.formulaId);
        const rows = !!(mt.rows.keyId || mt.rows.formulaId);
        return columns && rows;
    }

    beginRowColumnInputEdit(): void {
        this.captureMatrixTableBackup();
        this.isEditingRowColumnInputs = true;
        this.emitRowColumnInputEditActive();
    }

    cancelRowColumnInputEdit(): void {
        if (this.matrixTableEditBackup && this.table.matrixTable) {
            const restored = JSON.parse(JSON.stringify(this.matrixTableEditBackup)) as MatrixTable;
            this.table.matrixTable = restored;
        }
        this.matrixTableEditBackup = null;
        this.isEditingRowColumnInputs = false;
        this.matrixModelChanged.emit();
        this.emitRowColumnInputEditActive();
    }

    applyRowColumnInputs(): void {
        if (!this.table.matrixTable || !this.hasRowAndColumnInputs) {
            return;
        }
        this.matrixTableEditBackup = null;
        this.isEditingRowColumnInputs = false;
        this.syncCodeLookupBandsFromQuestions();
        this.resizeValuesPreservingOverlap();
        this.matrixModelChanged.emit();
        this.emitRowColumnInputEditActive();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes['openRowColumnInputStepOnce']?.currentValue === true
            && changes['openRowColumnInputStepOnce']?.previousValue !== true
        ) {
            this.captureMatrixTableBackup();
            this.isEditingRowColumnInputs = true;
            this.openRowColumnInputStepConsumed.emit();
            this.emitRowColumnInputEditActive();
        }
        const mt = this.table?.matrixTable;
        if (!mt) {
            this.priorValueGrid = { columnCount: 0, rowCount: 0 };
            return;
        }
        const columnCount = matrixAxisBinCount(mt.columns);
        const rowCount = matrixAxisBinCount(mt.rows);
        if (columnCount > 0 && rowCount > 0 && mt.values?.length === columnCount * rowCount) {
            this.priorValueGrid = { columnCount, rowCount };
        }
    }

    axisSummary(axis: MatrixAxis): string {
        return matrixAxisSummaryLabel(axis, this.summaryContext);
    }

    columnBinCount(mt: MatrixTable): number {
        return matrixAxisBinCount(mt.columns);
    }

    rowBinCount(mt: MatrixTable): number {
        return matrixAxisBinCount(mt.rows);
    }

    cellIndex(mt: MatrixTable, rowIndex: number, colIndex: number): number {
        return matrixValueIndex(this.columnBinCount(mt), rowIndex, colIndex);
    }

    columnIndices(mt: MatrixTable): number[] {
        const n = this.columnBinCount(mt);
        return n > 0 ? Array.from({ length: n }, (_, i) => i) : [];
    }

    rowIndices(mt: MatrixTable): number[] {
        const n = this.rowBinCount(mt);
        return n > 0 ? Array.from({ length: n }, (_, i) => i) : [];
    }

    columnHeaderId(colIndex: number): string {
        return `table-mx-col-${this.table.tableId}-${colIndex}`;
    }

    rowHeaderId(rowIndex: number): string {
        return `table-mx-row-${this.table.tableId}-${rowIndex}`;
    }

    bandHeaderLabel(axis: MatrixAxis, bandIndex: number): string {
        if (axis.breakpoints?.length) {
            return String(axis.breakpoints[bandIndex]);
        }
        if (axis.lookupValues?.length) {
            return axis.lookupValues[bandIndex] ?? '';
        }
        return '';
    }

    /** Band / input edits during the row+column step: notify parent for labels only. `values` is resized in `applyRowColumnInputs()` so breakpoints do not live-rewrite the grid. */
    onAxisBandsOrInputChanged(): void {
        this.matrixModelChanged.emit();
    }

    private emitRowColumnInputEditActive(): void {
        this.rowColumnInputEditActive.emit(this.isEditingRowColumnInputs);
    }

    private captureMatrixTableBackup(): void {
        const mt = this.table.matrixTable;
        this.matrixTableEditBackup = mt ? (JSON.parse(JSON.stringify(mt)) as MatrixTable) : null;
    }

    private codeChoiceValues(axis: MatrixAxis): string[] | undefined {
        if (!axis.keyId || axis.formulaId) {
            return undefined;
        }
        const q = this.questions.getQuestion(axis.keyId);
        if (q?.format !== 'Code') {
            return undefined;
        }
        return q.choices?.map((c) => c.value) ?? [];
    }

    private syncCodeLookupBandsFromQuestions(): void {
        const mt = this.table.matrixTable;
        if (!mt) {
            return;
        }
        for (const axis of [mt.rows, mt.columns]) {
            const choices = this.codeChoiceValues(axis);
            if (choices !== undefined) {
                syncMatrixAxisCodeLookupBands(axis, choices);
            }
        }
    }

    private resizeValuesPreservingOverlap(): void {
        const mt = this.table.matrixTable;
        if (!mt) {
            return;
        }
        const columnCount = matrixAxisBinCount(mt.columns);
        const rowCount = matrixAxisBinCount(mt.rows);
        if (!columnCount || !rowCount) {
            return;
        }
        const targetLen = columnCount * rowCount;
        const prevCols = this.priorValueGrid.columnCount;
        const prevRows = this.priorValueGrid.rowCount;
        const prev = mt.values ?? [];
        const next = new Array<number>(targetLen).fill(0);
        const copyRows = Math.min(prevRows, rowCount);
        const copyCols = Math.min(prevCols, columnCount);
        for (let r = 0; r < copyRows; r++) {
            for (let c = 0; c < copyCols; c++) {
                const flatSrc = r * prevCols + c;
                if (flatSrc < prev.length) {
                    next[matrixValueIndex(columnCount, r, c)] = prev[flatSrc];
                }
            }
        }
        mt.values = next;
        this.priorValueGrid = { columnCount, rowCount };
    }
}
